import moment from 'moment';

// Converts a sector string or array into an array of sector strings
export const getSectorsArray = (sectorString: string | string[]): string[] => {
	if (Array.isArray(sectorString)) {
		return sectorString;
	}
	return splitAndTrim(sectorString);
};


// Splits a comma-separated sector string and trims whitespace
export const splitAndTrim = (sectorString: string): string[] => {
	const sectors = sectorString.split(',');
	return sectors.map(s => s.trim());
};


//Expands an array of sectors, converting "AMESA/APAC" into ["AMESA", "APAC"]
export const expandSectors = (sectorsArray: string[]): string[] => {
	return sectorsArray.flatMap(expandSingleSector);
};


//Expands a single sector string, handling the special "AMESA/APAC" case
export const expandSingleSector = (sector: string): string[] => {
	if (sector === 'AMESA/APAC') {
		return ['AMESA', 'APAC'];
	}
	return [sector];
};


// Normalizes sectors to lowercase and returns them as a sorted, comma-separated string
export const normalizeAndSort = (sectors: string[]): string => {
	const lowercaseSectors = sectors.map(s => s.toLowerCase());
	const copiedSectors = [...lowercaseSectors];
	copiedSectors.sort((a: string, b: string) => a.localeCompare(b));
	return copiedSectors.join(',');
};


// Expands "AMESA/APAC" and normalizes for consistent comparison
export const normalizeSectorForComparison = (sectorString: string | string[] | undefined): string => {
	if (!sectorString) return '';
	
	const sectorsArray = getSectorsArray(sectorString);
	const expandedSectors = expandSectors(sectorsArray);
	const normalizedSectors = normalizeAndSort(expandedSectors);
	
	return normalizedSectors;
};

// Common helper function to format number values
export const formatValue = (value: number): string => {
	if (value >= 1000000) {
		return (value / 1000000).toFixed(1) + 'M';
	}
	if (value >= 1000) {
		return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
	}
	return value.toString();
};

// Helper function to calculate total for a period (moved outside to reduce nesting)
export const calculatePeriodTotal = (periodData: any, sectors: string[]): number => {
	let total = 0;
	sectors.forEach((sector: string) => {
		total += periodData[sector] || 0;
	});
	return total;
};

// Helper function to create label formatter (moved outside to reduce nesting)
export const createLabelFormatter = (rawData: any[], availableSectors: string[]) => {
	return function (params: any) {
		const periodData = rawData[params.dataIndex];
		const total = calculatePeriodTotal(periodData, availableSectors);
		return formatValue(total);
	};
};

// Helper function to convert moment to UTC ISO string
export const convertMomentToUTC = (momentObj: any): string => {
	if (!moment.isMoment(momentObj)) {
		return momentObj;
	}
	
	const year = momentObj.year();
	const month = momentObj.month();
	const date = momentObj.date();
	const hours = momentObj.hours();
	const minutes = momentObj.minutes();
	const seconds = momentObj.seconds();
	
	return moment.utc([year, month, date, hours, minutes, seconds]).toISOString();
};

// Helper function to process lastLogin filter
export const processLastLoginFilter = (filterValue: any[]): [string, string] => {
	const [startMoment, endMoment] = filterValue;
	
	console.log("DEBUG - Before conversion:", {
		startMoment: moment.isMoment(startMoment) ? startMoment.format('YYYY-MM-DD HH:mm:ss Z') : startMoment,
		endMoment: moment.isMoment(endMoment) ? endMoment.format('YYYY-MM-DD HH:mm:ss Z') : endMoment
	});
	
	const utcStart = convertMomentToUTC(startMoment);
	const utcEnd = convertMomentToUTC(endMoment);
	
	console.log("DEBUG - After conversion:", {
		utcStart,
		utcEnd
	});
	
	return [utcStart, utcEnd];
};

// Helper function to process a single filter key
export const processFilterKey = (key: string, value: any, updatedFilterInfo: any): void => {
	if (!value || value.length === 0) {
		delete updatedFilterInfo[key];
		return;
	}
	
	if (key === 'lastLogin' && value.length === 2) {
		updatedFilterInfo[key] = processLastLoginFilter(value);
	} else {
		updatedFilterInfo[key] = value;
	}
};

// Helper function to handle activity status change
export const handleActivityStatusChange = (
	activityStatus: any[],
	setUserStatusFilter: Function,
	setFilterInfo: Function,
	refreshFilterOptions: Function
): void => {
	if (!activityStatus || activityStatus.length === 0) {
		return;
	}
	
	const newStatus = activityStatus[0];
	setUserStatusFilter(newStatus);
	
	if (newStatus === "inactive") {
		setFilterInfo({});
	}
	
	setTimeout(() => {
		refreshFilterOptions(newStatus);
	}, 0);
};

// Helper function to determine selected sectors
export const getSelectedSectors = (
	checkedList: string[],
	allSectors: string[]
): string[] => {
	const normalizeSector = (sector: string) =>
		sector.toLowerCase() === 'europe' ? 'Europe' : sector;

	if (checkedList.length > 0 && checkedList[0].toLowerCase() !== 'all') {
		return checkedList.map(normalizeSector);
	}
	
	return allSectors.map(normalizeSector);
};

// Helper function to get search after value
export const getSearchAfterValue = (
	isPageJump: boolean,
	page: number,
	userStatusFilter: string | null | undefined,
	searchAfterMap: Map<number, any>
): any => {
	const shouldUseSearchAfter = !isPageJump && 
		page > 1 && 
		(userStatusFilter === 'inactive' || userStatusFilter === 'active');
	
	return shouldUseSearchAfter ? searchAfterMap.get(page - 1) : null;
};

// Interface for table data request parameters
export interface TableDataRequestParams {
	selectedSectors: string[];
	timeRange: string;
	userStatusFilter: string | null | undefined;
	page: number;
	pageSize: number;
	sortingInfo: any;
	filterInfo: any;
	searchDetails: string[];
	searchAfterValue: any;
	isPageJump: boolean;
}

// Helper function to build request body
export const buildTableDataRequest = (params: TableDataRequestParams): any => {
	const cleanedFilterInfo = { ...params.filterInfo };
	delete cleanedFilterInfo.activityStatus;

	return {
		sector: params.selectedSectors,
		filterOption: params.timeRange,
		userStatusFilter: params.userStatusFilter,
		page: params.page,
		pageSize: params.pageSize,
		sortBy: params.sortingInfo.sortBy,
		orderBy: params.sortingInfo.sortDir,
		filterBy: cleanedFilterInfo,
		searchTerm: params.searchDetails[0] || '',
		searchAfter: params.searchAfterValue,
		isPageJump: params.isPageJump
	};
};

// Helper function to replace table data completely
export const replaceTableData = (users: any[], setTableData: Function): void => {
	setTableData(users);
};

// Helper function to append data to existing table data
export const appendTableData = (users: any[], prevData: any[], setTableData: Function): void => {
	setTableData([...prevData, ...users]);
};

// Helper function to handle search after storage
export const handleSearchAfterStorage = (
	isReverseSorted: boolean,
	pageInfo: any,
	userStatusFilter: string | null | undefined,
	page: number,
	setSearchAfterMap: Function
): void => {
	const shouldStoreSearchAfter = !isReverseSorted && 
		pageInfo.searchAfter && 
		(userStatusFilter === 'inactive' || userStatusFilter === 'active');

	if (shouldStoreSearchAfter) {
		setSearchAfterMap((prev: Map<number, any>) => 
			new Map(prev).set(page, pageInfo.searchAfter)
		);
		console.log(`Stored searchAfter for page ${page}:`, pageInfo.searchAfter);
	} else if (isReverseSorted) {
		console.log('Skipping searchAfter storage for reverse sorted page');
		setSearchAfterMap(new Map());
	}
};